import React, { useState } from 'react';
import { UserProfile } from '../../../types/types';
import { AppSyncUser } from '../../../services/auth/types/user';
import Overview from './Overview';
import Trips from './Trips';
import Plan from './Plan';
import Settings from './Settings';
import TripDetail from './TripDetail';
import './MainContent.css';

interface MainContentProps {
  activeSection: string;
  user: UserProfile;
  appSyncUser: AppSyncUser | null;
  onLogout: () => Promise<void>;
  tripId?: string;
}

const MainContent: React.FC<MainContentProps> = ({ activeSection, user, appSyncUser, onLogout, tripId }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);

  const userName = appSyncUser?.firstName || user?.name || 'User';

  // Handle scroll for header transparency
  React.useEffect(() => {
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      if (target.scrollTop > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    const mainBody = document.querySelector('.main-body');
    if (mainBody) {
      mainBody.addEventListener('scroll', handleScroll);
      return () => mainBody.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const renderContent = () => {
    // If tripId is present, show TripDetail
    if (tripId) {
      return <TripDetail user={user} appSyncUser={appSyncUser} />;
    }

    // Otherwise render based on activeSection
    switch (activeSection) {
      case 'overview':
        return <Overview user={user} appSyncUser={appSyncUser} />;
      case 'trips':
        return <Trips user={user} appSyncUser={appSyncUser} />;
      case 'plan':
        return <Plan user={user} appSyncUser={appSyncUser} />;
      case 'settings':
        return <Settings user={user} appSyncUser={appSyncUser} onLogout={onLogout} />;
      default:
        return <Overview user={user} appSyncUser={appSyncUser} />;
    }
  };

  return (
    <div className="main-content">
      <div className="main-content-container">
      {/* Header - Only show on overview page */}
      {activeSection === 'overview' && !tripId && (
        <div className={`main-header ${isScrolled ? 'scrolled' : ''}`}>
          <div className="search-bar">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="search-icon">
              <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              placeholder="Search destinations, trips..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="main-body">
        {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default MainContent;